import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Calendar,
  Image,
  Users,
  User,
  LogOut,
  Eye,
  Menu,
  Clock,
  Monitor,
  BookUser,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../utils/api";

// Import section components
import OverviewSection from "./components/OverviewSection";
import EventsAndNewsSection from "./components/EventsAndNewsSection";
import GallerySection from "./components/GallerySection";
import OrganizationsSection from "./components/OrganizationsSection";
import UsersSection from "./components/UsersSection";
import ProfileSection from "./components/ProfileSection";
import ServicesSection from "./components/ServicesSection";
import HomePageAdsSection from "./components/HomePageAdsSection";
import ChurchDirectorySection from "./components/ChurchDirectorySection";

// Get initial tab from URL parameters or default to 'overview'
const getInitialTab = (search) => {
  const urlParams = new URLSearchParams(search);
  const tabParam = urlParams.get("tab");
  const validTabs = [
    "overview",
    "services",
    "eventsandnews",
    "gallery",
    "organizations",
    "users",
    "homepageads",
    "directory",
    "profile",
  ];
  return validTabs.includes(tabParam) ? tabParam : "overview";
};

// Get folder ID from URL parameters
const getFolderIdFromUrl = (search) => {
  const urlParams = new URLSearchParams(search);
  const folderId = urlParams.get("folder");
  return folderId ? parseInt(folderId) : null;
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(() =>
    getInitialTab(location.search),
  );
  const [currentFolderId, setCurrentFolderId] = useState(() =>
    getFolderIdFromUrl(location.search),
  );
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [totalGalleryCount, setTotalGalleryCount] = useState(0);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventLoading, setEventLoading] = useState(false);
  const [newsLoading, setNewsLoading] = useState(false);
  const [serviceLoading, setServiceLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showDirectoryModal, setShowDirectoryModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingNews, setEditingNews] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [editingDirectory, setEditingDirectory] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: "",
    description: "",
    date: "",
    time: "",
    location: "",
  });
  const [newsForm, setNewsForm] = useState({
    title: "",
    content: "",
    isPublished: false,
  });
  const [serviceForm, setServiceForm] = useState({
    name: "",
    day: "",
    time: "",
    description: "",
    language: "",
    category: "regular",
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [galleryForm, setGalleryForm] = useState({
    title: "",
    description: "",
    category: "others",
  });
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [galleryCategory, setGalleryCategory] = useState("all");
  const [currentFolder, setCurrentFolder] = useState(null);
  const [currentResourceFolder, setCurrentResourceFolder] = useState(null);
  const [currentResourceFolderId, setCurrentResourceFolderId] = useState(() => {
    // Check if we're on services tab and have a folder parameter
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get("tab");
    const folderId = urlParams.get("folder");
    return tab === "services" && folderId ? parseInt(folderId) : null;
  });
  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    password: "",
    isActive: true,
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogType, setDialogType] = useState("success"); // 'success' or 'error'
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState("");
  const [confirmDialogAction, setConfirmDialogAction] = useState(null);
  const [confirmDialogLoading, setConfirmDialogLoading] = useState(false);
  const [showImageViewModal, setShowImageViewModal] = useState(false);
  const [viewingImage, setViewingImage] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [orgForm, setOrgForm] = useState({
    organization_type: "",
    title: "",
    description: "",
    members: "",
    announcements: "",
  });
  const [directoryForm, setDirectoryForm] = useState({
    type: "individual",
    primaryMember: {
      name: "",
      dob: "",
      gender: "Male",
      contact_number: "",
      email: "",
      address: "",
    },
    additionalMembers: [],
  });

  // State for expandable rows in mobile view
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [expandedNews, setExpandedNews] = useState(new Set());
  const [expandedServices, setExpandedServices] = useState(new Set());
  const [expandedOrgs, setExpandedOrgs] = useState(new Set());
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [expandedDirectory, setExpandedDirectory] = useState(new Set());

  const showCustomDialog = (message, type = "success") => {
    setDialogMessage(message);
    setDialogType(type);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setDialogMessage("");
  };

  const showConfirmationDialog = (message, action) => {
    setConfirmDialogMessage(message);
    setConfirmDialogAction(() => action);
    setShowConfirmDialog(true);
  };

  // Helper functions for expandable rows
  const toggleExpanded = (id, expandedSet, setExpandedSet) => {
    const newSet = new Set(expandedSet);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setExpandedSet(newSet);
  };

  const closeConfirmDialog = () => {
    setShowConfirmDialog(false);
    setConfirmDialogMessage("");
    setConfirmDialogAction(null);
    setConfirmDialogLoading(false);
  };

  const handleConfirmAction = async () => {
    if (confirmDialogAction) {
      setConfirmDialogLoading(true);
      try {
        await confirmDialogAction();
        closeConfirmDialog();
      } catch (error) {
        setConfirmDialogLoading(false);
        // Error handling is done in the individual action functions
      }
    }
  };

  // Handle tab change and update URL
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const currentParams = new URLSearchParams(location.search);
    currentParams.set("tab", tabId);
    navigate(`${location.pathname}?${currentParams.toString()}`, {
      replace: true,
    });
  };

  // Listen for URL changes (browser back/forward navigation)
  useEffect(() => {
    const newTab = getInitialTab(location.search);
    const newFolderId = getFolderIdFromUrl(location.search);

    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }

    // Handle gallery folder synchronization
    if (newFolderId !== currentFolderId) {
      setCurrentFolderId(newFolderId);
      // Update currentFolder state based on folderId
      if (newFolderId && folders.length > 0) {
        const folder = folders.find((f) => f._id === newFolderId);
        setCurrentFolder(folder || null);
      } else {
        setCurrentFolder(null);
      }
    }

    // Handle resource folder synchronization
    if (newFolderId !== currentResourceFolderId) {
      setCurrentResourceFolderId(newFolderId);
      // Clear currentResourceFolder when folder parameter is removed from URL
      if (!newFolderId) {
        setCurrentResourceFolder(null);
      }
    }
  }, [
    location.search,
    activeTab,
    currentFolderId,
    currentResourceFolderId,
    folders,
  ]);

  useEffect(() => {
    fetchData();
    if (user) {
      setProfileForm({
        name: user.name || "",
        email: user.email || "",
        password: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Fetch gallery images when category filter or folder changes
  useEffect(() => {
    if (!loading) {
      fetchGalleryImages(galleryCategory, currentFolder?._id || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryCategory, currentFolder]);

  // Fetch folders when category changes
  useEffect(() => {
    if (!loading) {
      fetchFolders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryCategory]);

  // Initialize currentFolder from URL after folders are loaded
  useEffect(() => {
    if (currentFolderId && folders.length > 0 && !currentFolder) {
      const folder = folders.find((f) => f._id === currentFolderId);
      setCurrentFolder(folder || null);
    }
  }, [currentFolderId, folders, currentFolder]);

  // Restore resource folder from URL on services tab
  useEffect(() => {
    const fetchResourceFolders = async () => {
      if (
        activeTab === "services" &&
        currentResourceFolderId &&
        !currentResourceFolder
      ) {
        try {
          const response = await api.get("/api/service-resources/folders");
          const folder = response.data.find(
            (f) => f.id === currentResourceFolderId,
          );
          if (folder) {
            setCurrentResourceFolder(folder);
          }
        } catch (error) {
          console.error("Error fetching resource folder:", error);
        }
      }
    };

    fetchResourceFolders();
  }, [activeTab, currentResourceFolderId, currentResourceFolder]);

  const fetchGalleryImages = async (category = "all", folderId = null) => {
    try {
      setGalleryLoading(true);
      const params = new URLSearchParams();

      if (category && category !== "all") {
        params.append("category", category);
      }

      // Add folder filtering logic
      if (folderId === null) {
        // Fetch images not in any folder (main gallery view)
        params.append("folderId", "null");
      } else if (folderId) {
        // Fetch images in specific folder
        params.append("folderId", folderId.toString());
      }

      const galleryRes = await api.get(`/api/gallery?${params.toString()}`);
      setGallery(Array.isArray(galleryRes.data) ? galleryRes.data : []);
    } catch (error) {
      console.error("Error fetching gallery images:", error);
      setGallery([]);
    } finally {
      setGalleryLoading(false);
    }
  };

  const fetchTotalGalleryCount = async () => {
    try {
      // Fetch all gallery images without any filtering to get total count
      const galleryRes = await api.get("/api/gallery");
      setTotalGalleryCount(
        Array.isArray(galleryRes.data) ? galleryRes.data.length : 0,
      );
    } catch (error) {
      console.error("Error fetching total gallery count:", error);
      setTotalGalleryCount(0);
    }
  };

  const fetchFolders = async () => {
    try {
      const params = new URLSearchParams();
      if (galleryCategory && galleryCategory !== "all") {
        params.append("category", galleryCategory);
      }

      const response = await api.get(`/api/gallery/folders?${params}`);
      setFolders(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching folders:", error);
      setFolders([]);
    }
  };

  const fetchData = async () => {
    try {
      const [eventsRes, newsRes, servicesRes, usersRes, orgsRes, directoryRes] =
        await Promise.all([
          api.get("/api/events"),
          api.get("/api/news?admin=true"),
          api.get("/api/services").catch(() => ({ data: [] })),
          api.get("/api/users").catch(() => ({ data: [] })),
          api.get("/api/organizations").catch(() => ({ data: [] })),
          api.get("/api/church-directory").catch(() => ({ data: [] })),
        ]);
      setEvents(Array.isArray(eventsRes.data) ? eventsRes.data : []);
      setNews(Array.isArray(newsRes.data) ? newsRes.data : []);
      setServices(Array.isArray(servicesRes.data) ? servicesRes.data : []);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setOrganizations(Array.isArray(orgsRes.data) ? orgsRes.data : []);
      setDirectory(Array.isArray(directoryRes.data) ? directoryRes.data : []);

      // Fetch gallery images based on current category filter and folder context
      await fetchGalleryImages(galleryCategory, currentFolder?._id || null);

      // Fetch total gallery count for overview
      await fetchTotalGalleryCount();

      // Fetch folders
      await fetchFolders();
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    setEventLoading(true);
    try {
      if (editingEvent) {
        const response = await api.put(
          `/api/events/${editingEvent._id}`,
          eventForm,
        );
        setEvents(
          events.map((event) =>
            event._id === editingEvent._id ? response.data : event,
          ),
        );
        setEditingEvent(null);
      } else {
        const response = await api.post("/api/events", eventForm);
        setEvents([...events, response.data]);
      }
      setEventForm({
        title: "",
        description: "",
        date: "",
        time: "",
        location: "",
      });
      setShowEventModal(false);
      showCustomDialog("Event saved successfully!");
    } catch (error) {
      console.error("Error saving event:", error);
      showCustomDialog("Failed to save event", "error");
    } finally {
      setEventLoading(false);
    }
  };

  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    setNewsLoading(true);
    try {
      if (editingNews) {
        const response = await api.put(
          `/api/news/${editingNews._id}`,
          newsForm,
        );
        setNews(
          news.map((article) =>
            article._id === editingNews._id ? response.data : article,
          ),
        );
        setEditingNews(null);
      } else {
        const response = await api.post("/api/news", newsForm);
        setNews([...news, response.data]);
      }
      setNewsForm({
        title: "",
        content: "",
        isPublished: false,
      });
      setShowNewsModal(false);
      showCustomDialog("News article saved successfully!");
    } catch (error) {
      console.error("Error saving news article:", error);
      showCustomDialog("Failed to save news article", "error");
    } finally {
      setNewsLoading(false);
    }
  };

  const handleEventInputChange = (e) => {
    const { name, value } = e.target;
    setEventForm((prev) => {
      const newForm = {
        ...prev,
        [name]: value,
      };

      // If date is cleared, also clear the time
      if (name === "date" && !value) {
        newForm.time = "";
      }

      return newForm;
    });
  };

  const handleNewsInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewsForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title,
      description: event.description || "",
      date: event.date ? event.date.split("T")[0] : "",
      time: event.time || "",
      location: event.location || "",
    });
    setShowEventModal(true);
  };

  const handleDeleteEvent = (eventId) => {
    const deleteAction = async () => {
      try {
        await api.delete(`/api/events/${eventId}`);
        setEvents(events.filter((event) => event._id !== eventId));
        showCustomDialog("Event deleted successfully!");
      } catch (error) {
        console.error("Error deleting event:", error);
        showCustomDialog("Failed to delete event", "error");
      }
    };

    showConfirmationDialog(
      "Are you sure you want to delete this event?",
      deleteAction,
    );
  };

  const handleEditNews = (article) => {
    setEditingNews(article);
    setNewsForm({
      title: article.title,
      content: article.content,
      isPublished: article.isPublished,
    });
    setShowNewsModal(true);
  };

  const handleDeleteNews = (newsId) => {
    const deleteAction = async () => {
      try {
        await api.delete(`/api/news/${newsId}`);
        setNews(news.filter((article) => article._id !== newsId));
        showCustomDialog("News article deleted successfully!");
      } catch (error) {
        console.error("Error deleting news article:", error);
        showCustomDialog("Failed to delete news article", "error");
      }
    };

    showConfirmationDialog(
      "Are you sure you want to delete this news article?",
      deleteAction,
    );
  };

  // Service handling functions
  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    setServiceLoading(true);
    try {
      if (editingService) {
        const response = await api.put(
          `/api/services/${editingService.id}`,
          serviceForm,
        );
        setServices(
          services.map((service) =>
            service.id === editingService.id ? response.data : service,
          ),
        );
        setEditingService(null);
        showCustomDialog("Service updated successfully!");
      } else {
        const response = await api.post("/api/services", serviceForm);
        setServices([...services, response.data]);
        showCustomDialog("Service added successfully!");
      }

      setServiceForm({
        name: "",
        day: "",
        time: "",
        description: "",
        language: "",
        category: "regular",
      });
      setShowServiceModal(false);
    } catch (error) {
      console.error("Error saving service:", error);
      showCustomDialog("Failed to save service", "error");
    } finally {
      setServiceLoading(false);
    }
  };

  const handleServiceInputChange = (e) => {
    const { name, value } = e.target;
    setServiceForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditService = (service = null) => {
    setEditingService(service);
    if (service) {
      setServiceForm({
        name: service.name,
        day: service.day,
        time: service.time,
        description: service.description || "",
        language: service.language || "",
        category: service.category || "regular",
      });
    } else {
      setServiceForm({
        name: "",
        day: "",
        time: "",
        description: "",
        language: "",
        category: "regular",
      });
    }
    setShowServiceModal(true);
  };

  const handleDeleteService = (serviceId) => {
    const deleteAction = async () => {
      try {
        await api.delete(`/api/services/${serviceId}`);
        setServices(services.filter((service) => service.id !== serviceId));
        showCustomDialog("Service deleted successfully!");
      } catch (error) {
        console.error("Error deleting service:", error);
        showCustomDialog("Failed to delete service", "error");
      }
    };

    showConfirmationDialog(
      "Are you sure you want to delete this service?",
      deleteAction,
    );
  };

  const handleEditProfile = () => {
    setIsEditingProfile(true);
    setProfileForm({
      name: user.name || "",
      email: user.email || "",
      password: "",
    });
  };

  const handleCancelProfileEdit = () => {
    setIsEditingProfile(false);
    setShowPassword(false);
    setProfileForm({
      name: user.name || "",
      email: user.email || "",
      password: "",
    });
  };

  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    setProfileForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveProfile = async () => {
    setProfileLoading(true);
    try {
      const updateData = {
        name: profileForm.name,
        email: profileForm.email,
      };

      if (profileForm.password.trim()) {
        updateData.password = profileForm.password;
      }

      await api.put("/api/auth/profile", updateData);

      // Refresh user data from server
      const userResponse = await api.get("/api/auth/me");

      setIsEditingProfile(false);
      setShowPassword(false);
      setProfileForm({
        name: userResponse.data.name,
        email: userResponse.data.email,
        password: "",
      });
      showCustomDialog("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      showCustomDialog(
        error.response?.data?.message || "Failed to update profile",
        "error",
      );
    } finally {
      setProfileLoading(false);
    }
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setUserForm({
      name: userToEdit.name || "",
      email: userToEdit.email || "",
      password: "", // Don't populate password for editing
      isActive: userToEdit.isActive !== undefined ? userToEdit.isActive : true,
    });
    setShowUserModal(true);
  };

  const handleDeleteUser = (userToDelete) => {
    setUserToDelete(userToDelete);
    setShowDeleteConfirm(true);
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setUserLoading(true);
    try {
      if (editingUser) {
        // Edit existing user
        const updateData = {
          name: userForm.name,
          email: userForm.email,
          isActive: userForm.isActive,
        };

        const response = await api.put(
          `/api/users/${editingUser.id}`,
          updateData,
        );
        setUsers(
          users.map((u) => (u.id === editingUser.id ? response.data : u)),
        );
        setEditingUser(null);
        showCustomDialog("User updated successfully!");
      } else {
        // Add new user
        const newUserData = {
          name: userForm.name,
          email: userForm.email,
          password: userForm.password,
          role: "admin",
          isActive: true,
        };

        const response = await api.post("/api/users", newUserData);
        setUsers([response.data, ...users]);
        showCustomDialog("User added successfully!");
      }

      setUserForm({
        name: "",
        email: "",
        password: "",
        isActive: true,
      });
      setShowUserModal(false);
    } catch (error) {
      console.error("Error saving user:", error);
      showCustomDialog(
        error.response?.data?.message || "Failed to save user",
        "error",
      );
    } finally {
      setUserLoading(false);
    }
  };

  const confirmDeleteUser = async () => {
    try {
      await api.delete(`/api/users/${userToDelete.id}`);
      setUsers(users.filter((u) => u.id !== userToDelete.id));
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      showCustomDialog("User deleted successfully!");
    } catch (error) {
      console.error("Error deleting user:", error);
      showCustomDialog(
        error.response?.data?.message || "Failed to delete user",
        "error",
      );
    }
  };

  const handleUserInputChange = (e) => {
    const { name, value, type } = e.target;
    setUserForm((prev) => ({
      ...prev,
      [name]: type === "select-one" ? value === "true" : value,
    }));
  };

  // Directory handlers
  const handleEditDirectory = async (directoryEntry) => {
    setDirectoryLoading(true);
    try {
      const response = await api.get(
        `/api/church-directory/${directoryEntry.id}`,
      );
      const familyData = response.data;

      const primaryMember =
        familyData.members?.find((m) => m.is_primary) ||
        familyData.members?.[0];
      const additionalMembers =
        familyData.members?.filter((m) => !m.is_primary) || [];

      setDirectoryForm({
        type: familyData.is_individual ? "individual" : "family",
        primaryMember: {
          name: primaryMember?.name || "",
          dob: primaryMember?.dob ? primaryMember.dob.split("T")[0] : "",
          gender: primaryMember?.gender || "Male",
          contact_number: primaryMember?.contact_number || "",
          email: primaryMember?.email || "",
          address: primaryMember?.address || "",
        },
        additionalMembers: additionalMembers.map((m) => ({
          id: m.id,
          name: m.name,
          dob: m.dob ? m.dob.split("T")[0] : "",
          gender: m.gender,
          contact_number: m.contact_number || "",
          email: m.email || "",
        })),
      });

      setEditingDirectory(directoryEntry);
      setShowDirectoryModal(true);
    } catch (error) {
      console.error("Error loading directory entry:", error);
      showCustomDialog("Failed to load directory entry", "error");
    } finally {
      setDirectoryLoading(false);
    }
  };

  const handleDeleteDirectory = (directoryEntry) => {
    showConfirmationDialog(
      `Are you sure you want to delete this ${directoryEntry.is_individual ? "individual" : "family"}?`,
      async () => {
        try {
          await api.delete(`/api/church-directory/${directoryEntry.id}`);
          setDirectory(directory.filter((d) => d.id !== directoryEntry.id));
          showCustomDialog("Directory entry deleted successfully!");
        } catch (error) {
          console.error("Error deleting directory entry:", error);
          showCustomDialog("Failed to delete directory entry", "error");
        }
      },
    );
  };

  const handleDirectorySubmit = async (e) => {
    e.preventDefault();
    setDirectoryLoading(true);
    try {
      const primaryMember = {
        ...directoryForm.primaryMember,
        is_primary: true,
      };

      const allMembers = [
        primaryMember,
        ...directoryForm.additionalMembers.map((m) => ({
          ...m,
          is_primary: false,
        })),
      ];

      const payload = {
        is_individual: directoryForm.type === "individual",
        family_name:
          directoryForm.type === "individual"
            ? directoryForm.primaryMember.name
            : `${directoryForm.primaryMember.name} Family`,
        members: allMembers,
      };

      if (editingDirectory) {
        const response = await api.put(
          `/api/church-directory/${editingDirectory.id}`,
          payload,
        );
        setDirectory(
          directory.map((d) =>
            d.id === editingDirectory.id ? response.data : d,
          ),
        );
        setEditingDirectory(null);
        showCustomDialog("Directory entry updated successfully!");
      } else {
        const response = await api.post("/api/church-directory", payload);
        setDirectory([response.data, ...directory]);
        showCustomDialog("Directory entry added successfully!");
      }

      setDirectoryForm({
        type: "individual",
        primaryMember: {
          name: "",
          dob: "",
          gender: "Male",
          contact_number: "",
          email: "",
          address: "",
        },
        additionalMembers: [],
      });
      setShowDirectoryModal(false);
    } catch (error) {
      console.error("Error saving directory entry:", error);
      showCustomDialog(
        error.response?.data?.message || "Failed to save directory entry",
        "error",
      );
    } finally {
      setDirectoryLoading(false);
    }
  };

  // Callback functions for GallerySection
  const handleImageUploaded = (newImage) => {
    setGallery((prevGallery) => [newImage, ...prevGallery]);
    // Refresh total count after upload
    fetchTotalGalleryCount();
  };

  const handleMultipleImagesUploaded = (newImages) => {
    setGallery((prevGallery) => [...newImages, ...prevGallery]);
    // Refresh total count after upload
    fetchTotalGalleryCount();
  };

  const handleImageUpdated = (updatedImage) => {
    setGallery(
      gallery.map((img) => (img._id === updatedImage._id ? updatedImage : img)),
    );
    // No need to refresh total count for updates (count doesn't change)
  };

  const handleGalleryInputChange = (e) => {
    const { name, value } = e.target;
    setGalleryForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageFileChange = (e) => {
    setSelectedImageFile(Array.from(e.target.files));
  };

  const handleEditImage = (image) => {
    setEditingImage(image);
    setGalleryForm({
      title: image.title,
      description: image.description || "",
      category: image.category,
    });
    setSelectedImageFile(null);
    setShowGalleryModal(true);
  };

  const handleDeleteImage = (imageId) => {
    const deleteAction = async () => {
      try {
        await api.delete(`/api/gallery/${imageId}`);
        setGallery(gallery.filter((img) => img._id !== imageId));
        // Refresh total count after deletion
        fetchTotalGalleryCount();
        showCustomDialog("Image deleted successfully!");
      } catch (error) {
        console.error("Error deleting image:", error);
        showCustomDialog("Failed to delete image", "error");
      }
    };

    showConfirmationDialog(
      "Are you sure you want to delete this image?",
      deleteAction,
    );
  };

  const handleGalleryCategoryChange = (category) => {
    setGalleryCategory(category);
  };

  const handleEnterFolder = (folder) => {
    setCurrentFolder(folder);
    setCurrentFolderId(folder._id);

    // Update URL with folder parameter
    const currentParams = new URLSearchParams(location.search);
    currentParams.set("folder", folder._id.toString());
    navigate(`${location.pathname}?${currentParams.toString()}`, {
      replace: false,
    });
  };

  const handleExitFolder = () => {
    setCurrentFolder(null);
    setCurrentFolderId(null);

    // Remove folder parameter from URL
    const currentParams = new URLSearchParams(location.search);
    currentParams.delete("folder");
    navigate(`${location.pathname}?${currentParams.toString()}`, {
      replace: false,
    });
  };

  const handleEnterResourceFolder = (folder) => {
    setCurrentResourceFolder(folder);
    setCurrentResourceFolderId(folder.id);

    // Update URL with folder parameter (same as gallery)
    const currentParams = new URLSearchParams(location.search);
    currentParams.set("folder", folder.id.toString());
    navigate(`${location.pathname}?${currentParams.toString()}`, {
      replace: false,
    });

    // Scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleExitResourceFolder = () => {
    setCurrentResourceFolder(null);
    setCurrentResourceFolderId(null);

    // Remove folder parameter from URL
    const currentParams = new URLSearchParams(location.search);
    currentParams.delete("folder");
    navigate(`${location.pathname}?${currentParams.toString()}`, {
      replace: false,
    });
  };

  const handleViewImage = (image) => {
    setViewingImage(image);
    setShowImageViewModal(true);
  };

  const closeImageViewModal = () => {
    setShowImageViewModal(false);
    setViewingImage(null);
  };

  const handleOrgSubmit = async (e) => {
    e.preventDefault();
    setOrgLoading(true);
    try {
      if (editingOrg) {
        const response = await api.put(
          `/api/organizations/${editingOrg.id}`,
          orgForm,
        );
        setOrganizations(
          organizations.map((org) =>
            org.id === editingOrg.id ? response.data : org,
          ),
        );
        setEditingOrg(null);
        showCustomDialog("Organization updated successfully!");
      } else {
        const response = await api.post("/api/organizations", orgForm);
        setOrganizations([response.data, ...organizations]);
        showCustomDialog("Organization added successfully!");
      }

      setOrgForm({
        organization_type: "",
        title: "",
        description: "",
        members: "",
        announcements: "",
      });
      setShowOrgModal(false);
    } catch (error) {
      console.error("Error saving organization:", error);
      showCustomDialog(
        error.response?.data?.message || "Failed to save organization",
        "error",
      );
    } finally {
      setOrgLoading(false);
    }
  };

  const handleOrgInputChange = (e) => {
    const { name, value } = e.target;
    setOrgForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditOrg = (org) => {
    setEditingOrg(org);
    setOrgForm({
      organization_type: org.organization_type || "",
      title: org.title || "",
      description: org.description || "",
      members: org.members || "",
      announcements: org.announcements || "",
    });
    setShowOrgModal(true);
  };

  const handleDeleteOrg = (orgId) => {
    const deleteAction = async () => {
      try {
        await api.delete(`/api/organizations/${orgId}`);
        setOrganizations(organizations.filter((org) => org.id !== orgId));
        showCustomDialog("Organization deleted successfully!");
      } catch (error) {
        console.error("Error deleting organization:", error);
        showCustomDialog("Failed to delete organization", "error");
      }
    };

    showConfirmationDialog(
      "Are you sure you want to delete this organization?",
      deleteAction,
    );
  };

  const menuItems = [
    { id: "overview", label: "Overview", icon: Eye },
    { id: "directory", label: "Church Directory", icon: BookUser },
    { id: "services", label: "Services", icon: Clock },
    { id: "eventsandnews", label: "Events & News", icon: Calendar },
    { id: "gallery", label: "Gallery", icon: Image },
    { id: "organizations", label: "Organizations", icon: Users },
    { id: "users", label: "Users", icon: Users },
    { id: "profile", label: "Profile", icon: User },
    { id: "homepageads", label: "Home Page Ads", icon: Monitor },
    { id: "logout", label: "Logout", icon: LogOut },
  ];

  const renderOverview = () => (
    <OverviewSection
      events={events}
      news={news}
      gallery={{ length: totalGalleryCount }}
      organizations={organizations}
      users={users}
      setActiveTab={handleTabChange}
    />
  );

  const renderEventsAndNews = () => (
    <EventsAndNewsSection
      events={events}
      news={news}
      expandedEvents={expandedEvents}
      expandedNews={expandedNews}
      setExpandedEvents={setExpandedEvents}
      setExpandedNews={setExpandedNews}
      setEditingEvent={setEditingEvent}
      setEventForm={setEventForm}
      setShowEventModal={setShowEventModal}
      handleEditEvent={handleEditEvent}
      handleDeleteEvent={handleDeleteEvent}
      setEditingNews={setEditingNews}
      setNewsForm={setNewsForm}
      setShowNewsModal={setShowNewsModal}
      handleEditNews={handleEditNews}
      handleDeleteNews={handleDeleteNews}
      toggleExpanded={toggleExpanded}
      // Modal props
      showEventModal={showEventModal}
      editingEvent={editingEvent}
      eventForm={eventForm}
      eventLoading={eventLoading}
      handleEventSubmit={handleEventSubmit}
      handleEventInputChange={handleEventInputChange}
      showNewsModal={showNewsModal}
      editingNews={editingNews}
      newsForm={newsForm}
      newsLoading={newsLoading}
      handleNewsSubmit={handleNewsSubmit}
      handleNewsInputChange={handleNewsInputChange}
    />
  );

  const renderProfile = () => (
    <ProfileSection
      user={user}
      isEditingProfile={isEditingProfile}
      profileForm={profileForm}
      profileLoading={profileLoading}
      showPassword={showPassword}
      setShowPassword={setShowPassword}
      handleEditProfile={handleEditProfile}
      handleCancelProfileEdit={handleCancelProfileEdit}
      handleProfileInputChange={handleProfileInputChange}
      handleSaveProfile={handleSaveProfile}
    />
  );

  const renderGallery = () => (
    <GallerySection
      gallery={gallery}
      galleryLoading={galleryLoading}
      galleryCategory={galleryCategory}
      currentFolder={currentFolder}
      folders={folders}
      setEditingImage={setEditingImage}
      setGalleryForm={setGalleryForm}
      setSelectedImageFile={setSelectedImageFile}
      setShowGalleryModal={setShowGalleryModal}
      handleGalleryCategoryChange={handleGalleryCategoryChange}
      handleEnterFolder={handleEnterFolder}
      handleExitFolder={handleExitFolder}
      handleViewImage={handleViewImage}
      handleEditImage={handleEditImage}
      handleDeleteImage={handleDeleteImage}
      // Modal props
      showGalleryModal={showGalleryModal}
      editingImage={editingImage}
      galleryForm={galleryForm}
      selectedImageFile={selectedImageFile}
      handleGalleryInputChange={handleGalleryInputChange}
      handleImageFileChange={handleImageFileChange}
      showImageViewModal={showImageViewModal}
      viewingImage={viewingImage}
      closeImageViewModal={closeImageViewModal}
      // Callback functions
      onImageUploaded={handleImageUploaded}
      onMultipleImagesUploaded={handleMultipleImagesUploaded}
      onImageUpdated={handleImageUpdated}
      showCustomDialog={showCustomDialog}
      setGalleryLoading={setGalleryLoading}
      // Folder management
      setFolders={setFolders}
      fetchFolders={fetchFolders}
    />
  );

  const renderOrganizations = () => (
    <OrganizationsSection
      organizations={organizations}
      expandedOrgs={expandedOrgs}
      setExpandedOrgs={setExpandedOrgs}
      setEditingOrg={setEditingOrg}
      setOrgForm={setOrgForm}
      setShowOrgModal={setShowOrgModal}
      handleEditOrg={handleEditOrg}
      handleDeleteOrg={handleDeleteOrg}
      toggleExpanded={toggleExpanded}
      // Modal props
      showOrgModal={showOrgModal}
      editingOrg={editingOrg}
      orgForm={orgForm}
      orgLoading={orgLoading}
      handleOrgSubmit={handleOrgSubmit}
      handleOrgInputChange={handleOrgInputChange}
    />
  );

  const renderServices = () => (
    <ServicesSection
      services={services}
      setServices={setServices}
      expandedServices={expandedServices}
      setExpandedServices={setExpandedServices}
      setEditingService={setEditingService}
      setServiceForm={setServiceForm}
      setShowServiceModal={setShowServiceModal}
      handleEditService={handleEditService}
      handleDeleteService={handleDeleteService}
      toggleExpanded={toggleExpanded}
      // Modal props
      showServiceModal={showServiceModal}
      editingService={editingService}
      serviceForm={serviceForm}
      serviceLoading={serviceLoading}
      handleServiceSubmit={handleServiceSubmit}
      handleServiceInputChange={handleServiceInputChange}
      // Custom dialog function
      showCustomDialog={showCustomDialog}
      showConfirmationDialog={showConfirmationDialog}
      // Resource folder navigation
      currentResourceFolder={currentResourceFolder}
      handleEnterResourceFolder={handleEnterResourceFolder}
      handleExitResourceFolder={handleExitResourceFolder}
    />
  );

  const renderUsers = () => (
    <UsersSection
      users={users}
      user={user}
      expandedUsers={expandedUsers}
      setExpandedUsers={setExpandedUsers}
      setEditingUser={setEditingUser}
      setUserForm={setUserForm}
      setShowUserModal={setShowUserModal}
      handleEditUser={handleEditUser}
      handleDeleteUser={handleDeleteUser}
      toggleExpanded={toggleExpanded}
      // Modal props
      showUserModal={showUserModal}
      editingUser={editingUser}
      userForm={userForm}
      userLoading={userLoading}
      handleUserSubmit={handleUserSubmit}
      handleUserInputChange={handleUserInputChange}
      showDeleteConfirm={showDeleteConfirm}
      setShowDeleteConfirm={setShowDeleteConfirm}
      userToDelete={userToDelete}
      setUserToDelete={setUserToDelete}
      confirmDeleteUser={confirmDeleteUser}
    />
  );

  const renderHomePageAds = () => (
    <HomePageAdsSection
      showCustomDialog={showCustomDialog}
      showConfirmationDialog={showConfirmationDialog}
    />
  );

  const renderDirectory = () => (
    <ChurchDirectorySection
      directory={directory}
      expandedDirectory={expandedDirectory}
      setExpandedDirectory={setExpandedDirectory}
      handleEditDirectory={handleEditDirectory}
      handleDeleteDirectory={handleDeleteDirectory}
      toggleExpanded={toggleExpanded}
      // Modal props
      showDirectoryModal={showDirectoryModal}
      setShowDirectoryModal={setShowDirectoryModal}
      editingDirectory={editingDirectory}
      setEditingDirectory={setEditingDirectory}
      directoryForm={directoryForm}
      setDirectoryForm={setDirectoryForm}
      directoryLoading={directoryLoading}
      handleDirectorySubmit={handleDirectorySubmit}
    />
  );

  const renderContent = () => {
    switch (activeTab) {
      case "overview":
        return renderOverview();
      case "eventsandnews":
        return renderEventsAndNews();
      case "gallery":
        return renderGallery();
      case "organizations":
        return renderOrganizations();
      case "services":
        return renderServices();
      case "homepageads":
        return renderHomePageAds();
      case "users":
        return renderUsers();
      case "directory":
        return renderDirectory();
      case "profile":
        return renderProfile();
      default:
        return renderOverview();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Custom Dialog Modal */}
      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-auto">
            <div className="flex items-center mb-4">
              {dialogType === "success" ? (
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <svg
                    className="w-5 h-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              ) : (
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
              )}
              <h3 className="text-lg font-semibold text-gray-900">
                {dialogType === "success" ? "Success" : "Error"}
              </h3>
            </div>
            <p className="text-gray-700 mb-6">{dialogMessage}</p>
            <div className="flex justify-end">
              <button
                onClick={closeDialog}
                className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-auto">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                <svg
                  className="w-5 h-5 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Confirm Delete
              </h3>
            </div>
            <p className="text-gray-700 mb-6">{confirmDialogMessage}</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeConfirmDialog}
                disabled={confirmDialogLoading}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                disabled={confirmDialogLoading}
                className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {confirmDialogLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Header */}
      <div className="lg:hidden bg-white shadow-lg">
        <div className="flex items-center justify-between p-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Admin Dashboard</h2>
            <p className="text-xs text-gray-600">Welcome, {user?.name}</p>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 text-gray-600 hover:text-red-600 transition-colors"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="border-t border-gray-200 bg-white">
            <nav className="py-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      if (item.id === "logout") {
                        handleLogout();
                      } else {
                        handleTabChange(item.id);
                      }
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center px-4 py-3 text-left transition-colors ${
                      item.id === "logout"
                        ? "text-gray-600 hover:text-red-600 hover:bg-gray-50"
                        : activeTab === item.id
                          ? "bg-red-50 text-red-600"
                          : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </div>
        )}
      </div>

      <div className="flex">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block w-64 bg-white shadow-lg min-h-screen">
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-900">Admin Dashboard</h2>
            <p className="text-sm text-gray-600">Welcome, {user?.name}</p>
          </div>

          <nav className="mt-6">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (item.id === "logout") {
                      handleLogout();
                    } else {
                      handleTabChange(item.id);
                    }
                  }}
                  className={`w-full flex items-center px-6 py-3 text-left transition-colors ${
                    item.id === "logout"
                      ? "text-gray-600 hover:text-red-600"
                      : activeTab === item.id
                        ? "bg-red-50 text-red-600 border-r-2 border-red-600"
                        : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-3 sm:p-4 lg:p-8 min-w-0">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
